/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */
import { Fragment, isValidElement, memo, ReactNode, useMemo } from 'react';
import { useLanguage } from '@/providers/LanguageProvider';
import { formatTimestampToMonthDayTime } from '@/utils/formatedDates';
import { handleCopyLink } from '@/utils/handleCopyLink';
import { buildKahuLinkPath, parseKahuHref } from '@/utils/kahu-link-helpers';
import Icons from '@/ux-common/src/icons/icons';
import { formatNumberByLocale } from '@/ux-common/src/lib/utils';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { kahuCopyInfoType, KahuTurnStatus } from 'types/kahu';

import { Icons as AppIcons, KahuIcons } from '../icons/icons';
import { KahuPredefinedPromptChip } from './kahu-predefined-prompt-chip';
import { KahuResponseLink } from './kahu-response-link';
import { PromptList } from './prompt-list';
import { TraceList } from './trace-list';

/**
 * Insert zero-width <wbr> break opportunities after dots, underscores and
 * slashes so long dot-qualified identifiers (e.g.
 * `google.adk.agents.base_agent.BaseAgent.run_async`) wrap at their natural
 * separators in the narrow chat column, instead of being force-broken
 * mid-word by overflow-wrap (which produced "BaseAge|nt", "Fu|nctionTool").
 */
function insertWordBreaks(node: ReactNode): ReactNode {
  if (typeof node === 'string') {
    const segments = node.split(/([._/])/);
    if (segments.length <= 1) return node;
    return segments.map((seg, i) =>
      /^[._/]$/.test(seg) ? (
        <Fragment key={i}>
          {seg}
          <wbr />
        </Fragment>
      ) : (
        <Fragment key={i}>{seg}</Fragment>
      )
    );
  }
  if (Array.isArray(node)) {
    return node.map((child, i) => (
      <Fragment key={i}>{insertWordBreaks(child)}</Fragment>
    ));
  }
  return node;
}

type KahuResponseProps = {
  message: string;
  messageId: string;
  duration?: number;
  status?: KahuTurnStatus;
  interimMessage?: string;
  highlightedMessage?: ReactNode;
  copiedInfo: kahuCopyInfoType | null;
  setCopiedInfo: React.Dispatch<React.SetStateAction<kahuCopyInfoType | null>>;
  sendUserPrompt: (prompt: string) => void;
  followUpQuestions?: string[];
  tokenCount?: number;
  timestamp?: number;

  turnId?: string;
  feedback?: 'good' | 'bad';
  submitFeedback?: (rating: 'good' | 'bad') => Promise<void>;
};

const KahuResponseComponent = ({
  message,
  duration,
  status,
  interimMessage,
  highlightedMessage,
  copiedInfo,
  setCopiedInfo,
  messageId,
  sendUserPrompt,
  followUpQuestions,
  tokenCount,
  timestamp,
  turnId,
  feedback,
  submitFeedback,
}: KahuResponseProps) => {
  const isThinking = status === 'thinking' && !message;

  const isResponseCopied = copiedInfo?.occurrenceKey === messageId;

  // Only offer copy when there is text to copy (cancelled/incomplete turns arrive with empty content).
  const hasCopyableContent = Boolean(message?.trim());

  // Feedback stays available on every finished turn, including stopped ones.
  const canGiveFeedback = Boolean(turnId) && status !== 'thinking';

  const handleCopyResponse = (e: React.MouseEvent<HTMLButtonElement>) => {
    handleCopyLink(e, message, '', messageId, 'response', setCopiedInfo);
  };

  // Feedback is applied optimistically by the caller and reverted on failure.
  const handleFeedback = (rating: 'good' | 'bad') => {
    if (feedback === rating) return;
    void submitFeedback?.(rating);
  };

  function extractText(node: ReactNode): string {
    if (typeof node === 'string') return node;
    if (typeof node === 'number') return String(node);
    if (isValidElement(node)) return extractText(node.props.children);
    if (Array.isArray(node)) return node.map(extractText).join('');
    return '';
  }

  const { messages } = useLanguage();

  // Build the footer meta line ("Completed • 3.2s • 1,234 tokens • 11:05:44 am")
  const statusLabel =
    (status === 'completed' && messages?.KahuAI?.completed) ||
    (status === 'cancelled' && messages?.KahuAI?.cancelled) ||
    (status === 'incomplete' && messages?.KahuAI?.incomplete) ||
    '';

  // A cancelled/incomplete turn comes back with empty content, so show a reason in place of the blank message body.
  const emptyTurnNotice =
    (status === 'cancelled' && messages?.KahuAI?.stopped_by_user) ||
    (status === 'incomplete' && messages?.KahuAI?.stopped_incomplete) ||
    '';

  const metaSegments = [
    statusLabel,
    duration != undefined
      ? String(duration).endsWith('s')
        ? duration
        : `${duration}s`
      : '',
    tokenCount != undefined
      ? `${formatNumberByLocale(tokenCount)} ${messages?.KahuAI?.tokens}`
      : '',
    timestamp != undefined ? formatTimestampToMonthDayTime(timestamp) : '',
  ].filter(Boolean);

  const renderedMarkdown = useMemo(
    () => (
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        urlTransform={(url) => url}
        components={{
          p: ({ children }) => (
            <p className="text-body font-normal text-body-secondary">
              {children}
            </p>
          ),
          ol: ({ children, start }) => (
            <ol start={start} className="list-decimal space-y-1 pl-6">
              {children}
            </ol>
          ),
          ul: ({ children }) => (
            <ul className="list-disc space-y-1 pl-4">{children}</ul>
          ),
          li: ({ children }) => (
            <li className="text-body text-body-subtle">{children}</li>
          ),
          table: ({ children }) => (
            <div className="mt-1.5 max-w-full overflow-x-auto">
              <table className="w-full border-collapse border border-white/20">
                {children}
              </table>
            </div>
          ),
          thead: ({ children }) => <thead>{children}</thead>,
          th: ({ children }) => (
            <th className="border border-white/20 px-3 py-2 text-left font-semibold">
              {children}
            </th>
          ),
          td: ({ children }) => (
            <td className="border border-white/20 px-3 py-2">{children}</td>
          ),
          strong: ({ children }) => (
            <strong className="font-semibold text-body-primary">
              {insertWordBreaks(children)}
            </strong>
          ),
          pre: ({ children }) => <>{children}</>,
          code: ({ className, children }) => {
            if (className === 'language-okahu:prompt-list') {
              return (
                <PromptList
                  content={extractText(children)}
                  messageId={messageId}
                  copiedInfo={copiedInfo}
                  setCopiedInfo={setCopiedInfo}
                  isStreaming={status === 'thinking'}
                />
              );
            }
            if (
              className === 'language-okahu:trace-list' ||
              className === 'language-okahu:fact-list'
            ) {
              return (
                <TraceList
                  content={extractText(children)}
                  messageId={messageId}
                  copiedInfo={copiedInfo}
                  setCopiedInfo={setCopiedInfo}
                  isStreaming={status === 'thinking'}
                />
              );
            }
            return (
              <code className="whitespace-pre-wrap break-all rounded bg-black/30 px-1 py-0.5 text-sm">
                {children}
              </code>
            );
          },
          a: ({ children, href, node }) => {
            const parsed = parseKahuHref(href ?? '');
            const path = buildKahuLinkPath(parsed);
            const text = extractText(children);

            const offset = node?.position?.start?.offset ?? 0;

            const occurrenceKey = `${messageId}:${offset}`;
            return (
              <KahuResponseLink
                href={path ?? href ?? '#'}
                text={text}
                copiedInfo={copiedInfo}
                setCopiedInfo={setCopiedInfo}
                occurrenceKey={occurrenceKey}
              />
            );
          },
        }}
      >
        {message}
      </ReactMarkdown>
    ),
    [message, copiedInfo, setCopiedInfo, messageId, status]
  );

  return (
    <div className="flex flex-col gap-1">
      {isThinking ? (
        <>
          <div className="relative h-9 w-fit rounded-lg rounded-tl-none bg-[#FFFFFF1A] px-3 py-1">
            <span className="absolute right-full top-0 h-2 w-[6px] bg-[#FFFFFF1A] [clip-path:polygon(0_0,_100%_0,_100%_100%)]"></span>
            <div className="flex h-full items-center gap-1">
              <span className="size-1.5 animate-dotBounce rounded-full bg-[#FFFFFF7A]" />
              <span className="size-1.5 animate-dotBounce rounded-full bg-[#FFFFFF7A] [animation-delay:0.15s]" />
              <span className="size-1.5 animate-dotBounce rounded-full bg-[#FFFFFF7A] [animation-delay:0.3s]" />
            </div>
          </div>
          <p className="mt-1 text-body-sm italic text-form-input-disabled">
            {`${messages?.KahuAI?.thinking_this_through}...`}
          </p>
          <p className="mt-1 text-[13px] text-body-secondary">
            {interimMessage && `${interimMessage}...`}
          </p>
        </>
      ) : highlightedMessage ? (
        <div className="relative w-fit max-w-[332px] break-words py-1.5 pr-4">
          <p className="text-body font-normal text-body-secondary">
            {highlightedMessage}
          </p>
        </div>
      ) : (
        <>
          <div className="w-fit max-w-full break-words py-1.5 pr-4">
            {renderedMarkdown}
          </div>

          {emptyTurnNotice && (
            <p className="w-fit max-w-full py-1.5 pr-4 text-body text-body-secondary">
              {emptyTurnNotice}
            </p>
          )}

          <div className="flex gap-2">
            {/* Copy entire response */}
            {hasCopyableContent && (
              <button
                aria-label="Copy response"
                className="flex h-6 w-6 items-center justify-center rounded text-[#8C9EA1]"
                onClick={handleCopyResponse}
              >
                {isResponseCopied
                  ? Icons.CopyGreenCheckIcon('h-5 w-5')
                  : AppIcons.Copy(' h-5 w-5')}
              </button>
            )}
            {/* Feedback buttons (thumbs up/down) */}
            {canGiveFeedback && (
              <>
                <button
                  aria-label="Good response"
                  className="flex h-6 w-6 items-center justify-center rounded text-lg transition-colors"
                  onClick={() => handleFeedback('good')}
                  disabled={feedback === 'good'}
                >
                  {feedback === 'good'
                    ? KahuIcons.LikeSelectedIcon('h-5 w-5')
                    : KahuIcons.LikeDeselectedIcon('h-5 w-5')}
                </button>
                <button
                  aria-label="Bad response"
                  className="flex h-6 w-6 items-center justify-center rounded text-lg transition-colors"
                  onClick={() => handleFeedback('bad')}
                  disabled={feedback === 'bad'}
                >
                  {feedback === 'bad'
                    ? KahuIcons.DislikeSelectedIcon('h-5 w-5')
                    : KahuIcons.DislikeDeselectedIcon('h-5 w-5')}
                </button>
              </>
            )}
          </div>

          {/* response status, duration, token count and timestamp.
              Hidden while the response is still streaming. */}
          {status !== 'thinking' && metaSegments.length > 0 && (
            <div className="mt-1 inline-flex items-center gap-4 text-[#8C9EA1]">
              <span className="text-body-sm text-form-input-disabled">
                {metaSegments.join(' • ')}
              </span>
            </div>
          )}

          {/* Follow-up actions */}
          {followUpQuestions && followUpQuestions.length > 0 && (
            <div>
              <p className="mb-2 mt-3 text-body font-normal text-body-secondary">
                {messages?.KahuAI?.if_you_want_next_i_can_help_with}:
              </p>
              <div className="flex flex-col gap-1.5">
                {followUpQuestions.map((question) => (
                  <Fragment key={question}>
                    <KahuPredefinedPromptChip
                      prompt={question}
                      sendUserPrompt={sendUserPrompt}
                    />
                  </Fragment>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// only re-render when something this component actually displays changed.
const arePropsEqual = (prev: KahuResponseProps, next: KahuResponseProps) => {
  if (prev.message !== next.message) return false;
  if (prev.duration !== next.duration) return false;
  if (prev.messageId !== next.messageId) return false;
  if (prev.turnId !== next.turnId) return false;
  if (prev.feedback !== next.feedback) return false;
  if (prev.status !== next.status) return false;
  if (prev.interimMessage !== next.interimMessage) return false;
  if (prev.highlightedMessage !== next.highlightedMessage) return false;
  if (prev.copiedInfo !== next.copiedInfo) return false;
  if (prev.followUpQuestions !== next.followUpQuestions) return false;
  if (prev.tokenCount !== next.tokenCount) return false;
  if (prev.timestamp !== next.timestamp) return false;
  // setCopiedInfo is the raw useState setter —> guaranteed stable, ignore it.
  return true;
};

export const KahuResponse = memo(KahuResponseComponent, arePropsEqual);
